import { AiBorderBoxProps } from '@shared/components/AiBorderBox';
import type { LatLngLiteral } from '@shared/core/google-maps/types';

/**
 * Props for the AccommodationsTourProvider component
 */
export interface AccommodationsTourProviderProps
  extends Readonly<{
    /** Child components to render within the tour provider */
    children: React.ReactNode;
  }> {}

/**
 * Props for the HotelMapPinWithCard component
 */
export interface HotelMapPinWithCardProps
  extends Readonly<{
    /** Geographic location of the hotel/venue */
    location: LatLngLiteral;
    /** Force update trigger for map pin repositioning */
    forceUpdate?: string;
    /** Name of the location (used for venue) */
    name?: string;
    /** Display price for the hotel */
    price?: Maybe<string>;
    /** Original price for strike-through display */
    strikeOutPrice?: Maybe<string>;
    /** Whether to show the pin on top of the map */
    showOnTop?: boolean;
    /** Visual style intent for the border */
    intent?: AiBorderBoxProps['intent'];
  }> {}

/**
 * Props for the main map component that displays hotels and venue locations
 */
export interface MapProps
  extends Readonly<{
    /** List of origin points (hotels) to display on the map */
    originList: ReadonlyArray<LatLngLiteral>;
    /** Hotel property details excluding location */
    property: Omit<HotelMapPinWithCardProps, 'location'>;
    /** Destination point (venue location) to display on the map */
    destination: LatLngLiteral;
  }> {}

/**
 * Types of steps in the accommodations tour
 */
export type AccommodationsTourStepType = 'hotel' | 'guest';

/**
 * Order of steps in the accommodations tour
 */
export const STEP_ORDER: ReadonlyArray<AccommodationsTourStepType> = ['hotel', 'guest'] as const;

/**
 * Registration of a step in the accommodations tour
 */
export type AccommodationsTourStepRegistration = Readonly<{
  stepId: string;
  stepType: AccommodationsTourStepType;
  skipped?: boolean;
}>;

/**
 * Context value for the accommodations tour step context
 */
export type AccommodationsTourStepContextValue = Readonly<{
  registerStep: (stepType: AccommodationsTourStepType, stepId: string, skipped?: boolean) => void;
  registeredSteps: Readonly<Record<string, ReadonlyArray<AccommodationsTourStepRegistration>>>;
  tourName: string;
}>;

/**
 * State of the accommodations tour
 */
export interface AccommodationsTourState
  extends Readonly<{
    steps: Record<AccommodationsTourStepType, ReadonlyArray<AccommodationsTourStepRegistration>>;
  }> {}

/**
 * Action for the accommodations tour
 */
export type AccommodationsTourAction = Readonly<{
  type: 'ADD_STEP';
  stepType: AccommodationsTourStepType;
  stepId: string;
  skipped?: boolean;
}>;
